import { PROGRAM_CODE_MAP } from './dataNormalizer';

const mergeResolvedRow = (matchedRow, candidate) => {
  if (!matchedRow) return candidate;
  if (!candidate) return matchedRow;

  const merged = {
    ...matchedRow,
    ...candidate,
    productSelections: Array.isArray(candidate.productSelections)
      ? candidate.productSelections
      : matchedRow.productSelections,
    vendors: Array.isArray(candidate.vendors) ? candidate.vendors : matchedRow.vendors,
    _original: candidate._original ?? matchedRow._original,
  };

  const mergedKeys = Object.keys(merged);
  const candidateKeys = Object.keys(candidate);
  const sameShape = mergedKeys.length === candidateKeys.length;
  const sameValues = sameShape && mergedKeys.every((key) => merged[key] === candidate[key]);

  return sameValues ? candidate : merged;
};

/**
 * Resolve a row passed from a table into the "best" detail-row representation.
 *
 * Key rule: if a row is a Status Change (or otherwise carries a selectionKey context),
 * we MUST preserve it so the detail drawer can focus the correct selection by identity.
 */
export function resolveEnrollmentRowCandidate(candidate, filteredData = []) {
  if (!candidate) return null;

  // Preserve status-change/selection-context rows (they may contain selectionKey, previous/new status, etc).
  const id = candidate?.id != null ? String(candidate.id) : '';
  if (id.startsWith('status-change-')) return candidate;
  if (candidate?.selectionKey) return candidate;
  if (candidate?.changeId) return candidate;

  // Action Required / watchlist rows store the enrollment payload under `currentEnrollmentData`
  // (or `currentEnrollment`). The detail drawer expects the enrollment shape at the top-level.
  const nestedEnrollment = candidate?.currentEnrollmentData || candidate?.currentEnrollment || null;
  if (nestedEnrollment && typeof nestedEnrollment === 'object' && !Array.isArray(nestedEnrollment)) {
    const merged = { ...nestedEnrollment, ...candidate };
    merged.bac = merged.bac ?? nestedEnrollment?.bac ?? candidate?.bac ?? candidate?.dealerCd ?? null;
    merged.programName = merged.programName ?? nestedEnrollment?.programName ?? candidate?.programName ?? null;
    if (!merged.programCode && merged.programName) {
      merged.programCode = PROGRAM_CODE_MAP[merged.programName] || null;
    }
    if (!Array.isArray(merged.productSelections) && Array.isArray(nestedEnrollment.productSelections)) {
      merged.productSelections = nestedEnrollment.productSelections;
    }
    return merged;
  }

  const attemptId = candidate.id;
  if (attemptId) {
    const matchById = (filteredData || []).find((row) => row && row.id === attemptId);
    if (matchById) return mergeResolvedRow(matchById, candidate);
  }

  const bac = candidate.bac ?? candidate.dealerCd ?? null;
  const programCode =
    candidate.programCode ||
    candidate.program ||
    PROGRAM_CODE_MAP[candidate.programName] ||
    null;

  if (bac && programCode) {
    const matchByKey = (filteredData || []).find((row) => {
      if (!row) return false;
      if (row.bac !== bac) return false;
      const rowProgramCode = row.programCode || row.program || PROGRAM_CODE_MAP[row.programName] || null;
      return rowProgramCode === programCode;
    });
    if (matchByKey) return mergeResolvedRow(matchByKey, candidate);
  }

  return candidate;
}
